import { Injectable, ViewChild } from "@angular/core";
import {
    HttpEvent,
    HttpEventType,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { map, finalize } from "rxjs/operators";
import { SpinnerService } from '../services/spinner.service';
import { Router } from '@angular/router';
import { StateService } from 'app/profile/services/state.service';
import { StatusEnum, Status } from 'app/profile/models/state.model';
import { LocalStorageService } from '../services/index.service';
import { AuthData } from '@tesla/ng-auth';

@Injectable()
export class HttpProgressInterceptor implements HttpInterceptor {
    constructor(
        private spinnerService: SpinnerService,
        private routerService: Router,
        private storage: LocalStorageService,
        private stateService: StateService,
        private authData: AuthData) { }

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {

        let authReq = req.clone({
            reportProgress: true,
            headers: req.headers
                .set('Accept-Language', localStorage.getItem('Profile-Culture') || navigator.language)
        });

        if (this.authData.getAccessToken()?.idToken) {
            authReq = authReq.clone({
                headers: authReq.headers
                    .set('IdToken', 'Bearer ' + this.authData.getAccessToken()?.idToken)
            });
        }

        const mfaRequiredError = 'You are not authenticated for this application. Tesla Auth MFA is required.';
        const mfaInvalidSession = 'Your session is not valid, please re-login with MFA!';

        // send cloned request with header to the next handler.
        return next.handle(authReq)
            .pipe(
                map(event => this.manageRequestProgress(event)), // return last (completed) message to caller
                finalize(() => {
                    this.spinnerService.decrementCount();
                })
            ).catch(err => {
                this.spinnerService.stop();
                if (err.status === 401) {
                    if (err?.error?.error?.message?.includes(mfaRequiredError)) {
                        this.routerService.navigate(["auth/mfa-enrollment"]);
                    }
                    else if (err?.error?.error?.message?.includes(mfaInvalidSession)) {
                        this.routerService.navigate(["auth/mfa-invalid-session"]);
                    }
                    else {
                        // Ignore the redirect if the error is on Task
                        if (err?.url?.includes('Task')) {
                            return;
                        }
                        this.routerService.navigate(["auth/unauthorized"]);
                    }
                }
                else if (authReq.url.includes("Profile/MFA/GenerateOTP")) {
                    return throwError(err);
                } else if (authReq.url.includes('Onboarding/StepStatus') && authReq.method.toUpperCase() == 'POST') {
                    return throwError(err);
                } else if (authReq.url.includes("Profile/Translation")) return;
                else {
                    this.stateService.currentStatus.next(new Status(StatusEnum.Error, err?.error?.error?.message));
                    console.error(err);
                    return of(err);
                }
            });
    }

    private manageRequestProgress(event: HttpEvent<any>) {
        switch (event.type) {
            case HttpEventType.Sent:
                this.spinnerService.incrementCount();
                break;
            case HttpEventType.Response:
                return event;
            case HttpEventType.User:
            case HttpEventType.ResponseHeader:
            case HttpEventType.UploadProgress:
            case HttpEventType.DownloadProgress:
                // not handled event types.
                break;
            default:
                this.spinnerService.stop();
                break;
        }
    }
}
